import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import React from 'react';
import styles from './recent-suggestions-pills.module.scss';
import type {RecentShareSuggestionsProps} from '@Panels/share-panel/components/recent-suggestion-pills/recent-suggestion-pills.types';
import {SuggestionPill} from '@Components/template-submission-flow/components/suggestion-pill';
import {SuggestionPillType} from '@Components/template-submission-flow/components/suggestion-pill/suggestion-pill.types';
import {TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {ContributorInfo} from '@Panels/share-panel/share-panel.types';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

export function RecentSuggestionPills({...props}: RecentShareSuggestionsProps): ReactElement<any> {
  const showScrollIcons = props.showScrollIcons ?? false;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const updateArrows = (): void => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const {scrollLeft, scrollWidth, clientWidth} = container;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 1);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !showScrollIcons) return;

    const observer = new ResizeObserver(updateArrows);
    updateArrows();
    container.addEventListener('scroll', updateArrows);
    observer.observe(container);

    return (): void => {
      container.removeEventListener('scroll', updateArrows);
      observer.disconnect();
    };
  }, [showScrollIcons, props.recentSuggestions]);

  const scrollLeft = (): void => {
    scrollContainerRef.current?.scrollBy({left: -200, behavior: 'smooth'});
  };

  const scrollRight = (): void => {
    scrollContainerRef.current?.scrollBy({left: 200, behavior: 'smooth'});
  };

  const getLeftIcon = (): ReactElement<any> | undefined => {
    return showScrollIcons && showLeftArrow ? (
      <>
        <div className={styles.fadeLeft} />
        <Icon
          onClick={scrollLeft}
          size={IconSize.SIZE_ICON_16}
          className={`_position-absolute spacing-m-t-1 ${styles.iconLeft}`}
          icon="icon-caret-left"
          shape={IconShape.CIRCLE}
          type={IconType.SHADOW}
        />
      </>
    ) : undefined;
  };

  const getRightIcon = (): ReactElement<any> | undefined => {
    return showScrollIcons && showRightArrow ? (
      <>
        <div className={styles.fadeRight} />
        <Icon
          onClick={scrollRight}
          size={IconSize.SIZE_ICON_16}
          className={`_position-absolute spacing-m-t-1 ${styles.iconRight}`}
          icon="icon-caret-right"
          shape={IconShape.CIRCLE}
          type={IconType.SHADOW}
        />
      </>
    ) : undefined;
  };

  const getSuggestionPill = (item: ContributorInfo): ReactElement<any> => {
    if (props.showTooltip) {
      return (
        <OverlayTrigger
          overlay={<Tooltip className={`${styles.tooltip} spacing-m-b-2 _rounded-arrow`}>{item.email}</Tooltip>}
          trigger={['hover', 'focus']}
          key={`tooltip-${item.email}`}
        >
          <div className={styles.pillOverlay}>
            <SuggestionPill
              id={item.email}
              key={item.email}
              text={item.name ?? ''}
              onClick={() => {
                props.onRecentSuggestionPillClick(item.name ?? '', item.email);
              }}
              type={SuggestionPillType.NEUTRAL}
              className={`spacing-p-l-2 spacing-p-t-1 spacing-p-b-1 spacing-p-r-3 ${showScrollIcons ? styles.scrollableRecentItem : ''}`}
              hasLeftIcon="icon-plus"
              isTextBold={false}
              textSize={TextSize.XSMALL}
              hasHover
            />
          </div>
        </OverlayTrigger>
      );
    }

    return (
      <SuggestionPill
        id={item.email}
        key={item.email}
        text={item.name ?? ''}
        onClick={() => {
          props.onRecentSuggestionPillClick(item.name ?? '', item.email);
        }}
        type={SuggestionPillType.NEUTRAL}
        className={`spacing-p-l-2 spacing-p-t-1 spacing-p-b-1 spacing-p-r-3 ${showScrollIcons ? styles.scrollableRecentItem : ''}`}
        hasLeftIcon="icon-plus"
        isTextBold={false}
        textSize={TextSize.XSMALL}
        hasHover
      />
    );
  };

  return (
    <div className="_position-relative">
      {getLeftIcon()}
      <div className={`flex-h-row spacing-p-t-2 ${styles.recents} ${showScrollIcons ? styles.scrollableRecents : ''}`} ref={scrollContainerRef}>
        {props.recentSuggestions.slice(0, 3).map((item) => getSuggestionPill(item))}
      </div>
      {getRightIcon()}
    </div>
  );
}
